import React, { createContext, useEffect, useReducer, useContext } from 'react';
import * as turf from '@turf/turf';
import { useNavigate, useLocation } from 'react-router-dom';
import convert from 'color-convert';
import { v4 as uuidv4 } from 'uuid';
import {
  getRatings,
  getLifeStyleEssentials,
  login,
  logout,
  getNeighborhoodScore,
  getLifeStyleScores,
  fetchPlaceId,
  fetchPlaceDetails,
  getPlaceByLatLng,
  getProfilebyEmail,
  forgotPassword,
  confirmForgotPassword,
  signup,
  resendSignup,
  confirmSignup,
  verifyAPIKey,
  fetchGoogleRating,
  getNeighborhoodBounds,
  fetchNeighborhoodData,
  getCityBounds,
  getSubscriptionProfile,
  usageTracker,
  sendEmail,
  getAllNewsletters,
  logGoogleServicesUsage,
  getNeighborhoodByBorough,
  getQuizResults,
} from './api.js';
import propertiesData from './data/data.json';
import searchPropertiesData from './data/properties.json';
import { getArea, saveAddressToRecentlyViewed, getRadius } from './services.js';
import { categorizePlaceType } from './helper.js';
import useGoogleMaps from './useGoogleMaps.js';
import { sign } from 'chart.js/helpers';
import { getSignupEmailTemplate } from './templates/email';

import {
  setLSData,
  getLSData,
  setCookieValue,
  getCookieValue,
  useAutoLogout,
  convertMarkers,
  removeMarkerDuplicates,
  getDistanceFromRadius,
} from './services.js';
import { recommendedFilters } from './constants.js';
import { type } from '@testing-library/user-event/dist/type/index.js';

// Define initial state and reducer function
const initialState = {
  isGoogleMapsLoaded: false,
  map: null,
  isMapLoaded: null,
  currentMarker: Array(4).fill(null),
  showModal: false,
  isMobileSidebarOpen: false,
  isMenuOpen: false,
  isMobile: false,
  listOfProperties: propertiesData.filter((item) => item.type === 'Sale'),
  selectedProperty: propertiesData[0],
  searchPropertiesData: searchPropertiesData.streeteasy.properties.property,
  currentTab: 'lifestyle',
  mapTab: 'All',
  originalMarkers: [],
  listOfMarkers: [],
  List: [],
  currentDistance: '1',
  zoom: 14,
  mapType: 'satellite',
  currentPresence: '',
  currentRates: [],
  categoryFilters: [''],
  city: '',
  placename: '',
  showWalkIndexCompare: false,
  showCompareLifeStyle: false,
  lifeStyleScores: Array(4).fill({}),
  walkindexScores: Array(4).fill({}),
  token: '',
  compareAddress: Array(4).fill({}),
  compareLifeStyleAddress: Array(4).fill({}),
  scoreProperties: Array(4).fill(0),
  showEstablishments: false,
  showFilter: false,
  showRatingFilter: false,
  showDashboard: true,
  urlParams: false,
  currentMap: null,
  favorites: [],
  isDrawerCollapse: false,
  showFeedbackForm: false,
  isLoading: false,
  lifeStyleCompareMaps: Array(4).fill({}),
  keyPropertyName: String(propertiesData[1].property),
  currentWalkindxType: null,
  currentLifestyleType: [],
  isiPhone: false,
  currentCompareMap: { index: 0, visibleForm: false },
  compareMapsSliderIndex: 0,
  showMapStreetView: Array(4).fill(false),
  isStandalone: false,
  currentAddress: null,
  currentCity: '',
  isDesktopSidebarOpen: false,
  showSearchAddress: true,
  branding: '#000000',
  isPlugin: false,
  isFullScreenMap: false,
  isEstablishmentSearchOpen: false,
  establishmentSearchResult: {},
  currentView: 'Modern',
  enableChangeAddress: false,
  showTutorial: false,
  enableTutorial: false,
  showProperties: false,
  savedAddresses: [],
  showCombineSearch: false,
  selectedMap: 0,
  quickSearchMarkers: {
    markers: [],
    isLoading: true,
  },
  previousPage: [],
  isSmallerScreen: false,
  isMapLoading: Array(4).fill(false),
  reachedMaxLimit: false,
  isAddressInNYC: false,
  isDisclaimerOpen: false,
  errorMessage: '',
  userDetails: {},
  FPSubmitted: false,
  confirmFPSubmitted: false,
  signupSubmitted: false,
  confirmSignupSubmitted: false,
  apiKey: null,
  isInTrialMode: false,
  showScoreGuide: false,
  showMapMobileControls: false,
  showComparePopup: false,
  recentlyViewedAddresses: [],
  showPrivacyPolicy: false,
  clientName: null,
  isServerError: false,
  placeCardDetails: Array(4).fill(null),
  isDarkMode: true,
  showFavoriteList: false,
  isGoogleMode: false,
  showRecentHistoy: false,
  showCompareConfirmExit: false,
  selectedRecommendedFilters: [],
  showMapQS: false,
  showMarkerList: false,
  showAPIKeyScreen: false,
  isEmbedMode: false,
  setCenter: false,
  signupEmail: null,
  forgotPasswordEmail: null,
  showLegend: false,
  showEmailAuthGate: false,
  LSEmail: null,
  showCompareGraph: false,
  isAPIExpired: false,
  showErrorModal: false,
  isGooglePlacesLoaded: true,
  showCompareInputsGraph: false,
  showNeighborhoodBounds: false,
  neighborhoodBounds: null,
  showCityBounds: false,
  cityBounds: null,
  isAdmin: false,
  isExploreEnabled: false,
  selectedExploreEssential: null,
  signupTier: 'free',
  signupError: '',
  fromPricingPage: false,
  announcements: [],
  isExploreMenuOpen: false,
  fromLandingPage: false,
  showLimitModal: false,
  quizResults: { data: [], isLoading: false },
  neighborhoodList: { data: [], isLoading: false },
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'SET_IS_GOOGLE_MAPS_LOADED':
      return { ...state, isGoogleMapsLoaded: action.payload };

    case 'SET_MAP_INSTANCE':
      return { ...state, map: action.payload };

    case 'SET_COMPARE_MAPS':
      const mapIndex = action.payload.index;
      const mapObject = action.payload.object;
      const updatedCompareMaps = [...state.lifeStyleCompareMaps];
      updatedCompareMaps[mapIndex] = mapObject;
      return { ...state, lifeStyleCompareMaps: updatedCompareMaps };

    case 'SET_CURRENT_MARKER':
      const { markerIndex, markerData } = action.payload;
      const newCurrentMarker = [...state.currentMarker];
      newCurrentMarker[markerIndex] = markerData;
      return { ...state, currentMarker: newCurrentMarker };
    case 'GET_PROPERTY_BY_COORDINATES':
      const { latitude, longitude } = action.payload;

      // Find property based on criteria
      const foundProperty = state.listOfProperties.find(
        (item) => item.latitude === latitude && item.longitude === longitude
      );

      return {
        ...state,
        selectedProperty: foundProperty || state.listOfProperties[0],
      };

    case 'GET_PROPERTY_BY_NAME':
      const propertyName = action.payload;
      const property = state.listOfProperties.find((prop) =>
        prop.property.toLowerCase().includes(propertyName.toLowerCase())
      );

      const getFinalProperty =
        property?.length > 1
          ? property[0]
          : property || state.listOfProperties[0];

      return {
        ...state,
        selectedProperty: getFinalProperty,
      };
    case 'SET_CURRENT_TAB':
      return {
        ...state,
        currentTab: action.payload,
        mapTab: action.payload === 'walkindex' ? 'Nature' : 'All',
      };
    case 'SET_PROPERTY_LIST':
      return {
        ...state,
        listOfProperties: action.payload,
        selectedProperty: action.payload[0],
      };
    case 'SET_MOBILE_SIDEBAR':
      return { ...state, isMobileSidebarOpen: action.payload };
    case 'SET_SHOW_LEGEND':
      return { ...state, showLegend: action.payload };
    case 'SET_DESKTOP_SIDEBAR':
      return { ...state, isDesktopSidebarOpen: action.payload };
    case 'SET_MENU_SIDEBAR':
      return { ...state, isMenuOpen: action.payload };
    case 'SET_IS_MOBILE':
      return { ...state, isMobile: action.payload };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_DRAWER':
      return { ...state, isDrawerCollapse: action.payload };
    case 'SET_COMPARE_VISIBLE':
      return { ...state, showWalkIndexCompare: action.payload };
    case 'SET_COMPARE_LIFESTYLE':
      return { ...state, showCompareLifeStyle: action.payload };
    case 'SET_SHOW_ESTABLISHMENTS':
      return { ...state, showEstablishments: action.payload };
    case 'SET_SHOW_FILTERS':
      return { ...state, showFilter: action.payload };
    case 'SET_SHOW_RATING_FILTER':
      return { ...state, showRatingFilter: action.payload };
    case 'SET_LIST_OF_MARKERS':
      const { markersIndex, markersList } = action.payload;

      const updatedListOfMarkers = [...state.listOfMarkers];

      updatedListOfMarkers[markersIndex] = markersList;
      return { ...state, listOfMarkers: updatedListOfMarkers };
    case 'SET_LIST_OF_ORIGINAL_MARKERS':
      return { ...state, originalMarkers: action.payload };
    case 'SET_CURRENT_DISTANCE':
      return { ...state, currentDistance: action.payload };
    case 'SET_CURRENT_ZOOM':
      return { ...state, zoom: action.payload };
    case 'SET_CENTER':
      return { ...state, setCenter: !state.setCenter };
    case 'SET_CURRENT_MAPTYPE':
      return { ...state, mapType: action.payload };
    case 'SET_WALKINDEX':
      return { ...state, walkIndexList: action.payload };
    case 'SET_CURRENT_PRESENCE':
      return { ...state, currentPresence: action.payload };
    case 'SET_LIFESTYLE_SCORES':
      const { scoreIndex, scoreData } = action.payload;

      const newLifeStyleScores = [...state.lifeStyleScores];

      newLifeStyleScores[scoreIndex] = scoreData;

      return {
        ...state,
        lifeStyleScores: newLifeStyleScores,
        isLoading: false,
      };
    case 'SET_WALKINDEX_SCORES':
      const { walkindxScoreData, walkindxScoreIndex } = action.payload;

      const newWalkindexScores = [...state.walkindexScores];

      newWalkindexScores[walkindxScoreIndex] = walkindxScoreData;

      return {
        ...state,
        walkindexScores: newWalkindexScores,
        isLoading: false,
      };
    case 'SET_CITY':
      return { ...state, city: action.payload };
    case 'SET_MAP':
      return { ...state, currentMap: action.payload };
    case 'SET_RATES':
      return { ...state, currentRates: action.payload };

    case 'SEARCH_CATEGORY':
      const newCategoryFilters = [...state.categoryFilters];
      newCategoryFilters[0] = action.payload;

      return {
        ...state,
        categoryFilters: newCategoryFilters,
      };
    case 'APPEND_CATEGORY':
      return {
        ...state,
        categoryFilters: [...state.categoryFilters, ...action.payload],
      };
    case 'REMOVE_CATEGORY':
      return {
        ...state,
        categoryFilters: state.categoryFilters.filter(
          (value) => !action.payload.includes(value)
        ),
      };
    case 'UPDATE_CATEGORY':
        const { index: categoryIndex, value } = action.payload;
        const updatedCategoryFilters = [...state.categoryFilters];
        updatedCategoryFilters[categoryIndex] = value;

        return {
          ...state,
          categoryFilters: updatedCategoryFilters,
        };
    case 'RESET_RATE':
      return {
        ...state,
        currentRates: action.payload,
      };
    case 'RESET_CATEGORY':
      return {
        ...state,
        categoryFilters: action.payload,
      };
    case 'SET_PLACENAME':
      return { ...state, placename: action.payload };
    case 'SET_PARAMS':
      return { ...state, urlParams: action.payload };
    case 'SET_MAP_TAB':
      return { ...state, mapTab: action.payload };
    case 'SET_SHOW_MODAL':
      return { ...state, showModal: action.payload };
    case 'SET_SHOW_DASHBOARD':
      return { ...state, showDashboard: action.payload };
    case 'SET_TOKEN':
      return { ...state, token: action.payload };
    case 'SET_API_KEY':
      return { ...state, apiKey: action.payload };

    case 'UPDATE_LIFESTYLE_COMPARE_ADDRESS':
      const { item, address } = action.payload;

      // Create a copy of the compareAddress array
      const compareAddress = [...state.compareLifeStyleAddress];

      // Set the object at the desired index
      compareAddress[item] = address;

      // Return the updated state
      return {
        ...state,
        compareLifeStyleAddress: compareAddress,
      };

    case 'RESET_LIFESTYLE_COMPARE_ADDRESS':
      const newArray = Array(4).fill({});
      newArray[0] = action.payload;
      return { ...state, compareLifeStyleAddress: newArray };

    case 'RESET_LIFESTYLE_COMPARE_MAPS':
      const newlifeStyleCompareMaps = Array(4).fill({});
      return { ...state, lifeStyleCompareMaps: newlifeStyleCompareMaps };

    case 'UPDATE_COMPARE_ADDRESS':
      const { index, object } = action.payload;

      const updatedCompareAddress = [...state.compareAddress];

      updatedCompareAddress[index] = object;

      return {
        ...state,
        compareAddress: updatedCompareAddress,
      };

    case 'RESET_COMPARE_ADDRESS':
      const emptyArray = Array(4).fill({});
      emptyArray[0] = action.payload;
      return { ...state, compareAddress: emptyArray };

    case 'UPDATE_COMPARE':
      const { number, length } = action.payload;

      const addressList = [...state.scoreProperties];

      addressList[number] = length;

      return {
        ...state,
        scoreProperties: addressList,
      };
    case 'SET_FAVORITES':
      return { ...state, favorites: action.payload };

    case 'SET_SHOW_FEEDBACK_FORM':
      return { ...state, showFeedbackForm: action.payload };

    case 'SET_KEY_PROPERTY_NAME':
      return { ...state, keyPropertyName: action.payload };

    case 'SET_CURRENT_WALKINDX_TYPE':
      return { ...state, currentWalkindxType: action.payload };

    case 'SET_CURRENT_LIFESTYLE_TYPE':
      const newCurrentLifestyleType = [
        ...state.currentLifestyleType,
        action.payload,
      ];
      return { ...state, currentLifestyleType: newCurrentLifestyleType };

    case 'REMOVE_CURRENT_LIFESTYLE_TYPE':
      const filteredCurrentLifestyleType = state.currentLifestyleType.filter(
        (type) => type !== action.payload
      );

      return { ...state, currentLifestyleType: filteredCurrentLifestyleType };

    case 'RESET_CURRENT_LIFESTYLE_TYPE':
      return { ...state, currentLifestyleType: [] };

    case 'SET_IS_IPHONE':
      return { ...state, isiPhone: action.payload };

    case 'SET_CURRENT_COMPARE_MAP':
      return { ...state, currentCompareMap: action.payload };

    case 'SET_COMPARE_MAPS_SLIDER_INDEX':
      return { ...state, compareMapsSliderIndex: action.payload };

    case 'SET_SHOW_MAP_STREET_VIEW':
      const { svIndex, svValue } = action.payload;
      const updatedShowMapStreetView = [...state.showMapStreetView];

      updatedShowMapStreetView[svIndex] = svValue;
      return { ...state, showMapStreetView: updatedShowMapStreetView };

    case 'SET_IS_STANDALONE':
      return { ...state, isStandalone: action.payload };

    case 'SET_CURRENT_ADDRESS':
      return { ...state, currentAddress: action.payload };

    case 'SET_CURRENT_CITY':
      return { ...state, currentCity: action.payload };
    case 'SET_SHOW_SEARCH_ADDRESS':
      return { ...state, showSearchAddress: action.payload };
    case 'SET_BRANDING':
      return { ...state, branding: action.payload };
    case 'SET_IS_PLUGIN':
      return { ...state, isPlugin: action.payload };
    case 'SET_IS_FULL_SCREEN_MAP':
      return { ...state, isFullScreenMap: action.payload };
    case 'SET_IS_ESTABLISHMENT_SEARCH_OPEN':
      return { ...state, isEstablishmentSearchOpen: action.payload };
    case 'SET_ESTABLISHMENT_SEARCH_RESULT':
      return { ...state, establishmentSearchResult: action.payload };
    case 'SET_CURRENT_VIEW':
      return { ...state, currentView: action.payload };
    case 'SET_ENABLE_CHANGE_ADDRESS':
      return { ...state, enableChangeAddress: action.payload };
    case 'SET_SHOW_TUTORIAL':
      return { ...state, showTutorial: action.payload };
    case 'SET_ENABLE_TUTORIAL':
      return { ...state, enableTutorial: action.payload };
    case 'SET_SHOW_PROPERTIES':
      return { ...state, showProperties: action.payload };
    case 'SET_SAVED_ADDRESSES':
      return { ...state, savedAddresses: action.payload };
    case 'SET_SHOW_COMBINE_SEARCH':
      return { ...state, showCombineSearch: action.payload };
    case 'SET_SELECTED_MAP':
      return { ...state, selectedMap: action.payload };
    case 'SET_QUICK_SEARCH_MARKERS':
      return { ...state, quickSearchMarkers: action.payload };
    case 'SET_PREVIOUS_PAGE':
      const newPreviousPage = [...state.previousPage, action.payload];
      return { ...state, previousPage: newPreviousPage };
    case 'FILTER_PREVIOUS_PAGE':
      return { ...state, previousPage: action.payload };
    case 'SET_IS_SMALLER_SCREEN':
      return { ...state, isSmallerScreen: action.payload };
    case 'SET_IS_MAP_LOADING':
      const { loadingIndex, loadingValue } = action.payload;

      const updatedIsMapLoading = [...state.isMapLoading];

      updatedIsMapLoading[loadingIndex] = loadingValue;
      return { ...state, isMapLoading: updatedIsMapLoading };
    case 'SET_REACHED_MAX_LIMIT':
      return { ...state, reachedMaxLimit: action.payload };
    case 'SET_IS_ADDRESS_IN_NYC':
      return { ...state, isAddressInNYC: action.payload };
    case 'SET_IS_DISCLAIMER_OPEN':
      return { ...state, isDisclaimerOpen: action.payload };
    case 'SET_ERROR_MESSAGE':
      return { ...state, errorMessage: action.payload };
    case 'SET_USER_DETAILS':
      return { ...state, userDetails: action.payload };
    case 'SET_FORGOT_PASSWORD_SUBMITTED':
      return { ...state, FPSubmitted: action.payload };
    case 'SET_CONFIRM_FORGOT_PASSWORD_SUBMITTED':
      return { ...state, confirmFPSubmitted: action.payload };
    case 'SET_SIGNUP_SUBMITTED':
      return { ...state, signupSubmitted: action.payload };
    case 'SET_CONFIRM_SIGNUP_SUBMITTED':
      return { ...state, confirmSignupSubmitted: action.payload };
    case 'SET_IS_IN_TRIAL_MODE':
      return { ...state, isInTrialMode: action.payload };
    case 'SET_SHOW_SCORE_GUIDE':
      return { ...state, showScoreGuide: action.payload };
    case 'SET_SHOW_MAP_MOBILE_CONTROLS':
      return { ...state, showMapMobileControls: action.payload };
    case 'SET_SHOW_COMPARE_POPUP':
      return { ...state, showComparePopup: action.payload };
    case 'SET_RECENTLY_VIEWED_ADDRESSES':
      return { ...state, recentlyViewedAddresses: action.payload };
    case 'SET_SHOW_PRIVACY_POLICY':
      return { ...state, showPrivacyPolicy: action.payload };
    case 'SET_CLIENT_NAME':
      return { ...state, clientName: action.payload };
    case 'SET_IS_SERVER_ERROR':
      return { ...state, isServerError: action.payload };
    case 'SET_PLACE_CARD_DETAILS':
      const { placeIndex, placeData } = action.payload;
      const newPlaceCard = [...state.placeCardDetails];
      newPlaceCard[placeIndex] = placeData;
      return { ...state, placeCardDetails: newPlaceCard };
    case 'SET_IS_DARK_MODE':
      return { ...state, isDarkMode: action.payload };
    case 'SET_SHOW_FAVORITE_LIST':
      return { ...state, showFavoriteList: action.payload };
    case 'SET_IS_GOOGLE_MODE':
      return { ...state, isGoogleMode: action.payload };
    case 'SET_SHOW_RECENT_HISTORY':
      return { ...state, showRecentHistoy: action.payload };
    case 'SET_SHOW_COMPARE_CONFIRM_EXIT':
      return { ...state, showCompareConfirmExit: action.payload };
    case 'SET_SELECTED_RECOMMENDED_FILTERS':
      return { ...state, selectedRecommendedFilters: action.payload };
    case 'SET_SHOW_MAP_QS':
      return { ...state, showMapQS: action.payload };
    case 'SET_SHOW_MARKER_LIST':
      return { ...state, showMarkerList: action.payload };
    case 'SET_SHOW_API_KEY_SCREEN':
      return { ...state, showAPIKeyScreen: action.payload };
      case 'SET_EMBED_MODE':
        console.log("SET_EMBED_MODE", action.payload);
        return { ...state, isEmbedMode: action.payload };
    case 'SET_SIGNUP_EMAIL':
      return { ...state, signupEmail: action.payload };
    case 'SET_FORGOT_PASSWORD_EMAIL':
      return { ...state, forgotPasswordEmail: action.payload };
    case 'SET_SHOW_EMAIL_AUTH_GATE':
      return { ...state, showEmailAuthGate: action.payload };
    case 'SET_LS_EMAIL':
      return { ...state, LSEmail: action.payload };
    case 'SET_SHOW_COMPARE_GRAPH':
        return { ...state, showCompareGraph: action.payload };
    case 'SET_IS_API_EXPIRED':
      return { ...state, isAPIExpired: action.payload };
    case 'SET_SHOW_ERROR_MODAL':
      return { ...state, showErrorModal: action.payload };
    case 'SET_IS_GOOGLE_PLACES_LOADED':
      return { ...state, isGooglePlacesLoaded: action.payload };
    case 'SET_SHOW_COMPARE_INPUTS_GRAPH':
      return { ...state, showCompareInputsGraph: action.payload };
    case 'SET_SHOW_NEIGHBORHOOD_BOUNDS':
      return {
        ...state,
        showNeighborhoodBounds: action.payload,
        showCityBounds: action.payload ? false : state.showCityBounds,
      }
    case 'SET_SHOW_CITY_BOUNDS':
      return {
        ...state,
        showCityBounds: action.payload,
        showNeighborhoodBounds: action.payload ? false : state.showNeighborhoodBounds,
      }
    case 'SET_NEIGHBORHOOD_BOUNDS':
      return { ...state, neighborhoodBounds: action.payload };
    case 'SET_CITY_BOUNDS':
      return { ...state, cityBounds: action.payload };
    case 'SET_IS_ADMIN':
      return { ...state, isAdmin: action.payload };
    case 'SET_IS_EXPLORE_ENABLED':
      return { ...state, isExploreEnabled: action.payload };
    case 'SET_SELECTED_EXPLORE_ESSENTIAL':
      return { ...state, selectedExploreEssential: action.payload };
    case 'SET_SIGNUP_TIER':
      return { ...state, signupTier: action.payload };
    case 'SET_SIGNUP_ERROR':
      return { ...state, signupError: action.payload };
    case 'SET_FROM_PRICING_PAGE':
      return { ...state, fromPricingPage: action.payload };
    case 'SET_ANNOUNCEMENTS':
      return { ...state, announcements: action.payload };
    case 'SET_IS_EXPLORE_MENU_OPEN':
      return { ...state, isExploreMenuOpen: action.payload };
    case 'SET_FROM_LANDING_PAGE':
      return { ...state, fromLandingPage: action.payload };
    case 'SET_SHOW_LIMIT_MODAL':
      return { ...state, showLimitModal: action.payload };
    case 'SET_QUIZ_RESULTS':
      return { ...state, quizResults: action.payload };
    case 'SET_NEIGBORHOOD_LIST':
      return { ...state, neighborhoodList: action.payload };
    default:
      return state;
  }
};

// Create the store context
export const StoreContext = createContext();
export const useStore = () => useContext(StoreContext);

// Create the store provider component
export const StoreProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const navigate = useNavigate();

  const fullURL = window.location.href;
  const queryString = fullURL?.split('?')[1];
  const searchParams = queryString
    ? new URLSearchParams(queryString?.replace(/#/g, '%23'))
    : new URLSearchParams(window.location.search);
  const isGoogleMapsReady = useGoogleMaps();
  const location = useLocation();

  const radiusParam = searchParams.get('radius');

  // const store = useStore();

  const {
    token,
    currentDistance,
    map,
    showCompareLifeStyle,
    compareLifeStyleAddress,
    lifeStyleCompareMaps,
    searchPropertiesData,
    currentTab,
    currentAddress,
    branding,
    isFullScreenMap,
    isEstablishmentSearchOpen,
    currentView,
    previousPage,
    isMapLoading,
    apiKey,
    errorMessage,
    isInTrialMode,
    isMobile,
    clientName,
    showDashboard,
    userDetails,
    recentlyViewedAddresses,
    LSEmail,
    showNeighborhoodBounds,
    showCityBounds,
    isPlugin,
    showErrorModal,
    isAdmin,
    isExploreEnabled,
    signupError,
    signupTier,
    signupEmail,
    showLimitModal,
  } = state;

  useEffect(() => {
    if (isGoogleMapsReady) {
      dispatch({
        type: 'SET_IS_GOOGLE_MAPS_LOADED',
        payload: isGoogleMapsReady,
      });
    }
  }, [isGoogleMapsReady]);

  const appendSuffix = () => {
    if (isPlugin) {
      return clientName;
    };
  };

  const isInFullAccessSites = () => {
    const sites = [
      'rickyhgroup.com',
      'omegarealestateglobal.com',
      'ccrny.com',
      'localhost',
      '192.168.254.101',
    ];
    const currentURL = window.location.hostname;
    return sites.includes(currentURL);
  };

  const handleLoading = (index=0, value) => {
    if (showDashboard) {
      dispatch({ type: 'SET_LOADING', payload: value });
    } else {
      dispatch({
        type: 'SET_IS_MAP_LOADING',
        payload: { loadingIndex: index, loadingValue: value },
      });
    }
  };

  const setAPIKey = () => {
    // const isUnlimited = searchParams.get('isUnlimited');
    const isGoogleAds = searchParams.get('isGoogleAds');

    let payload = '';
    if (isGoogleAds === 'true') {
      payload = '4B94311833CC53166A3ED242E5AD7';
      dispatch({ type: 'SET_IS_GOOGLE_MODE', payload: true });
    } else {
      payload = 'ED7BE647FE3863A21C224876254A4';
    }

    dispatch({ type: 'SET_API_KEY', payload: payload });
    // const selectedKey = isUnlimited === 'true' || isInFullAccessSites()
    // ? 'ED7BE647FE3863A21C224876254A4'
    // : '4B94311833CC53166A3ED242E5AD7';
  };

  const isKeyExpired = (userData) => {
    const expirationDate = new Date(userData?.api_key_details?.expires_at);
    const now = new Date();
    return (now > expirationDate) && !userData?.api_key_details?.never_expires;
  };

  const handleReachedLimit = (data) => {
    if (data?.is_superuser || clientName !== null) return false;

    if (
      data?.subscription?.remaining_credits < 0 ||
      data?.subscription?.is_active === false ||
      (data?.subsciprtion?.end_date && new Date(data?.subscription?.end_date) < new Date()) ||
      data?.detail === "Search credit limit reached"
    ) {
      return true;
    }
    return false;
  };

  const excludedPaths = [
    '/',
    '/home-test',
    '/announcements',
    '/platform',
    '/contact-us',
    '/about-us',
    '/address/',
    '/services/',
    '/pricing',
    '/book-demo',
    '/profile',
    '/faqs',
    '/login',
    '/plugin',
    '/explore',
    '/activate',
    '/test-landing',
    '/test-about-us',
    '/chat',
    '/quiz'
  ];

  const isExcludedPath = excludedPaths.some((path) =>
    location.pathname.startsWith(path)
  );

  const handleLogin = async (data) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });

      const params = new URLSearchParams(window.location.search);
      const userEmail = params?.get('email') || getLSData('Agent-Email');
      const apiKey = params?.get('api_key');
      const nextLink = params?.get('next') || getLSData('old-link-visited');

      if ((apiKey || userEmail) && !isAdmin) {
        const user = await getSubscriptionProfile(userEmail);
        if (user) {
          setLSData('user-data', user);
          const companySlug = user?.company?.slug;
          setLSData('Company-Slug', companySlug);

          const email = user?.email;
          setLSData('Agent-Email', email);

          const respApi = user?.['api_key'];
          dispatch({ type: 'SET_USER_DETAILS', payload: user });
          setLSData('user-token', apiKey); // Use API key as a "token" in this case

          if (handleReachedLimit(user)) {
            handleLimitModal();
            return;
          }

          if (!isBeta() && !isCompass() && !isExcludedPath) {
            navigate('/');
          }
        } else {
          dispatch({ type: 'SET_ERROR_MESSAGE', payload: 'Failed to fetch user profile using API key.' });
        }
      } else {
        try {
          const response = await login(data);
          if (response.status !== 200) {
            // Login failed, handle error
            dispatch({ type: 'SET_ERROR_MESSAGE', payload: response?.detail || 'Login failed.' });
          } else {
            // Login successful
            setLSData('user-data', response.data);
            setLSData('Agent-Email', response.data?.email);

            const company = response.data?.company?.slug;
            if (company) {
              setLSData('Company-Slug', company);
            }
            if (isPlugin && clientName) {
              const agent = searchParams.get('agentemail');
              const slug = searchParams.get('companyslug');
              setLSData('Company-Slug', slug);
              const res = await getSubscriptionProfile(agent);

              if (handleReachedLimit(res)) {
                handleLimitModal();
                return;
              }
            }

            // Response data contains the user details already no need to call the api again
            dispatch({ type: 'SET_USER_DETAILS', payload: response.data });

            // Redirect to home if not on address page
            if (!location.pathname.startsWith('/address/') && !isBeta() && !isCompass()) {
              if (nextLink) {
                navigate(nextLink);
                setLSData('old-link-visited', '');
              } else {
                navigate('/');
              }
            }
          }
        } catch (error) {
          console.error('Error during login:', error);
        }
      } 
    } catch (error) {
      console.error('Error in handleLogin:', error);
    } finally {
      if (showLimitModal) {
        dispatch({ type: 'SET_SHOW_LIMIT_MODAL', payload: false });
      }
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const handleLogout = async () => {
    const response = await logout();
    if (response.Success) {
      dispatch({ type: 'SET_TOKEN', payload: '' });
      dispatch({ type: 'SET_USER_DETAILS', payload: {} });
      setLSData('user-token', null);
      setLSData('user-details', null);
      navigate('/login');
    }
  };

  const handleAPIKey = async (data) => {
    const params = new URLSearchParams(window.location.search);
    const userEmail = params.get('email');
    const email = userDetails?.email;
    data['email'] = userEmail || email;
    const response = await verifyAPIKey(token, data);
    if (response.status === 200) {
      dispatch({ type: 'SET_SHOW_API_KEY_SCREEN', payload: false });
      setLSData('user-api-key', data.api_key);
    } else {
      dispatch({ type: 'SET_ERROR_MESSAGE', payload: response.data.detail });
    }
  };

  const PRICING_CONSTANT = {
    "Free Tier": 10,
    "Premium": 20,
    "Enterprise": 50,
  };

  const handleLimitModal = () => {
    dispatch({ type: 'SET_SHOW_LIMIT_MODAL', payload: true });
    const currentLink = decodeURIComponent(location.pathname + location.search);
    setLSData('old-link-visited', currentLink);
  };

  const handleUsageTracker = async (search_type, query, object, check_only = false) => {
    const isFree = !hasUserData();
    const query_params = handleQueryParams(search_type, object);
    let trackerPayload;
    if (isFree) {
      trackerPayload = { search_type, query, check_only };
    } else {
      trackerPayload = { search_type, query, check_only };
      if (search_type !== 'compare') {
        trackerPayload['query_params'] = query_params;
      }
    }
    const response = await usageTracker(trackerPayload, isFree);

    if (response?.status === 200) {
      if (search_type === 'address') {
        const plan = userDetails?.subscription?.plan;
        const remaining = response?.data?.searches_remaining;
        const is_admin = userDetails?.is_staff || userDetails?.is_superuser;
        const creditLimit = PRICING_CONSTANT[plan] ?? 0;
        const search_count = response?.data?.search_count || 0;

        if (isFree) {
          if (search_count > 3 || remaining < 0) {
            handleLimitModal();
          }
        } else if (!is_admin && (remaining < 0 || search_count > creditLimit)) {
          handleLimitModal();
          return;
        }
      }
    } else {
      if (response?.status === 403 && isFree) {
        clearErrorMessage();
        handleLimitModal();
      }
      // dispatch({ type: 'SET_ERROR_MESSAGE', payload: response?.data?.detail });
    }
  };

  const handleQueryParams = (search_type, query) => {
    const params = {
      "city": query?.city,
      "state": query?.state,
      "country": query?.country,
    };

    switch (search_type) {
      case 'quicksearch':
        params['establishment_address'] = query;
        return params;
      case 'filter':
        params['address'] = query?.property || '';
        params['category'] = query?.category || '';
        params['sub-category'] = query?.subcategory || '';
        return params;
      case 'compare':
        return;
      case 'gem':
      case 'favorite':
        params['address'] = query?.property || '';
        params['establishment_address'] = query.marker;
        params['category'] = query?.type || '';
        params['sub-category'] = query?.category || '';
        return params;
      case 'characterwalkingradius':
      case 'gemwalkingradius':
      case 'gemcategory':
      case 'charactercategory':
        params['address'] = query?.property || '';
        return params;
      default:
        return params;
    }
  }

  const handleGoogleMarkerRating = async (index, data) => {
    try {
      handleLoading(index, true);
      const email = userDetails?.email || LSEmail;
      const response = await fetchGoogleRating(data, email);
      return response;
    } catch (error) {
      console.log(error);
    } finally {
      handleLoading(index, false);
    }
  };

  const resetLogoutTimer = useAutoLogout(
    30 * 60 * 1000,
    handleLogout,
    isInTrialMode
  );

  const handleForgotPassword = async (data) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    const response = await forgotPassword(data);

    const isSubmitted = response.status === 201;
    dispatch({
      type: 'SET_FORGOT_PASSWORD_SUBMITTED',
      payload: isSubmitted,
    });

    if (response.status !== 201) {
      dispatch({
        type: 'SET_ERROR_MESSAGE',
        payload: response?.data?.non_field_errors?.[0] || 'Invalid email address'
      });
    }

    dispatch({ type: 'SET_LOADING', payload: false });
  };

  const handleConfirmForgotPassword = async (data) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    const response = await confirmForgotPassword(data);

    const isSubmitted = response.status === 200;
    dispatch({
      type: 'SET_CONFIRM_FORGOT_PASSWORD_SUBMITTED',
      payload: isSubmitted,
    });

    if (!isSubmitted) {
      dispatch({
        type: 'SET_ERROR_MESSAGE',
        payload: response.data?.non_field_errors?.[0] || 'Invalid reset link.'
      });
    }

    dispatch({ type: 'SET_LOADING', payload: false });
  };

  const handleSignup = async (data) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    const response = await signup(data);

    const isSubmitted = response.status === 200;
    dispatch({
      type: 'SET_SIGNUP_SUBMITTED',
      payload: isSubmitted,
    });

    if (!isSubmitted) {
      dispatch({
        type: 'SET_SIGNUP_ERROR',
        payload: response?.data?.error?.[0] || 'Something went wrong. Please try again later.'
      });
    }

    dispatch({ type: 'SET_LOADING', payload: false });
  };

  const handleResendSignup = async (data) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    const response = await resendSignup(data);

    const isSubmitted = response.Success;
    dispatch({
      type: 'SET_SIGNUP_SUBMITTED',
      payload: isSubmitted,
    });

    if (!isSubmitted) {
      dispatch({ type: 'SET_ERROR_MESSAGE', payload: response.Error });
    }

    dispatch({ type: 'SET_LOADING', payload: false });
  };

  const handleConfirmSignup = async (data) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    const response = await confirmSignup(data);

    const isSubmitted = response.status === 200;
    dispatch({
      type: 'SET_CONFIRM_SIGNUP_SUBMITTED',
      payload: isSubmitted,
    });

    if (!isSubmitted) {
      dispatch({
        type: 'SET_SIGNUP_ERROR',
        payload: response?.data?.error || 'Something went wrong. Please try again later.'
      });
    } else {
      const payload = {
        email: 'walkspanllc@gmail.com',
        subject: 'New Registration',
        html: getSignupEmailTemplate(response?.data?.email, signupTier)
      }
      await sendEmail(payload);

      setLSData('signup-email', null);
      dispatch({ type: 'SET_SIGNUP_EMAIL', payload: null });
      dispatch({ type: 'SET_SIGNUP_TIER', payload: 'free' });
    }

    dispatch({ type: 'SET_LOADING', payload: false });
  };

  const elem = document.documentElement;
  const requestFullScreen =
    elem?.requestFullscreen ||
    elem?.mozRequestFullScreen ||
    elem?.webkitRequestFullscreen ||
    elem?.msRequestFullscreen;

  const exitFullScreen =
    document?.exitFullscreen ||
    document?.mozCancelFullScreen ||
    document?.webkitExitFullscreen ||
    document?.msExitFullscreen;

  const toggleFullScreenMap = () => {
    const isFullScreen =
      document?.fullscreenElement ||
      document?.mozFullScreenElement ||
      document?.webkitFullscreenElement ||
      document?.msFullscreenElement ||
      document?.webkitCurrentFullScreenElement;

    if (!isFullScreen) {
      requestFullScreen?.call(elem);
      sendDataToGoogleAnalytics('used_fullscreen', {});
    } else {
      exitFullScreen.call(document);
    }
    dispatch({ type: 'SET_IS_FULL_SCREEN_MAP', payload: !isFullScreenMap });
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (isFullScreenMap && event.key === 'Escape') {
        event.preventDefault();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isFullScreenMap]);

  const getFavoriteMarkers = () => {
    const favorites = getLSData('favorites');
    dispatch({
      type: 'SET_FAVORITES',
      payload: favorites ? favorites : [],
    });
  };

  const getSavedAddresses = () => {
    const addresses = getLSData('addresses');
    dispatch({
      type: 'SET_SAVED_ADDRESSES',
      payload: addresses ? addresses : [],
    });
  };

  const getRecentlyViewed = () => {
    const addresses = getLSData('recenlty-viewed');
    dispatch({
      type: 'SET_RECENTLY_VIEWED_ADDRESSES',
      payload: addresses ? addresses : [],
    });
  }

  const fetchWalkIndex = async () => {
    const markers = await getNeighborhoodScore('New York', 'NY', '', userDetails?.email);

    dispatch({ type: 'SET_WALKINDEX', payload: markers });
  };

  const setMapCenter = () => {
    if (map && !showCompareLifeStyle) {
      const { latitude, longitude } = currentAddress;
      const newPosition = new window.google.maps.LatLng(
        parseFloat(latitude),
        parseFloat(longitude)
      );

      map.panTo(newPosition);
    } else {
      dispatch({
        type: 'SET_CENTER',
        payload: true
      });
     
      // lifeStyleCompareMaps.forEach((compareMap, index) => {
      //   if (Object.keys(compareMap).length !== 0) {
      //     const property = compareLifeStyleAddress[index];
      //     const { latitude, longitude } = property;
      //     const newPosition = new window.google.maps.LatLng(
      //       parseFloat(latitude),
      //       parseFloat(longitude)
      //     );
      //     console.log("MAP", compareMap)
      //     if (compareMap) {
      //       compareMap?.panTo(newPosition);
      //     }   
      //   }
      // });
    }
  };

  const getAddressImage = () => {
    const hasData = searchPropertiesData.filter((searchProperty) => {
      const address = searchProperty.location.address.toLowerCase();
      const apartment = searchProperty.location.apartment.toLowerCase();
      return currentAddress?.property
        .toLowerCase()
        .includes(address + ' #' + apartment);
    });

    return hasData.length > 0 ? hasData[0].media.photo[0]?._url : false;
  };

  const fetchLifeStyleScores = async (
    address,
    index,
    shouldReturn=false,
  ) => {
    handleLoading(index, true);
    try {
      const { latitude, longitude } = address || currentAddress;
      const isFree = !hasUserData();
      let scores = await getLifeStyleScores(
        latitude,
        longitude,
        getArea(currentDistance),
        userDetails?.email || LSEmail,
        appendSuffix(),
        isFree,
      );

      if (scores?.status !== 200) {
        if (!showErrorModal) {
          dispatch({ type: 'SET_ERROR_MESSAGE', payload: scores?.data?.error || scores?.data?.detail });
          if (handleReachedLimit(scores?.data)) {
            handleLimitModal();
            return;
          }
          // dispatch({ type: 'SET_SHOW_ERROR_MODAL', payload: true });
        }
      } else {
        if (scores?.error === "50x") {
          dispatch({ type: 'SET_IS_SERVER_ERROR', payload: true });
          handleLoading(index, false);
        }

        const extractedScores = scores.data.reduce((acc, item) => {
          acc[item.type] = item.count;
          return acc;
        }, {});

        if (shouldReturn) return extractedScores;

        const payload = {
          scoreData: extractedScores,
          scoreIndex: index || 0,
        };
        dispatch({ type: 'SET_LIFESTYLE_SCORES', payload: payload });
      }
    } catch (error) {
      console.log(error);
    } finally {
      handleLoading(index, false);
    }
  };

  const fetchWalkIndexRatings = async (
    address,
    index = 0,
    shouldReturn=false,
  ) => {
    handleLoading(index, true);
    try {
      const { latitude, longitude } = address || currentAddress;
      const isFree = !hasUserData();
      let scores = await getRatings(
        latitude,
        longitude,
        getArea(currentDistance),
        userDetails?.email || LSEmail,
        appendSuffix(),
        isFree,
      );

      if (scores?.status !== 200) {
        if (!showErrorModal) {
          dispatch({ type: 'SET_ERROR_MESSAGE', payload: scores?.data?.error || scores?.data?.detail });
          if (handleReachedLimit(scores?.data)) {
            handleLimitModal();
            return;
          }
          // dispatch({ type: 'SET_SHOW_ERROR_MODAL', payload: true });
        }
      } else {
        if (scores?.error === "50x") {
          dispatch({ type: 'SET_IS_SERVER_ERROR', payload: true });
          handleLoading(index, false);
        }
  
        const isGoogleAds = searchParams.get('isGoogleAds') === 'true';
  
        if (scores) {
          if (!scores.Success && !Array.isArray(scores) && isGoogleAds) {
            if (scores.Error === 'Exceeded access limit') {
              dispatch({ type: 'SET_REACHED_MAX_LIMIT', payload: true });
            } else if (scores.Error === 'Unauthorized') {
              dispatch({ type: 'SET_TOKEN', payload: '' });
              dispatch({ type: 'SET_USER_DETAILS', payload: {} });
              navigate('/login');
            }
          } else {
            if (shouldReturn) return scores.data[0];
  
            const payload = {
              walkindxScoreData: scores.data[0],
              walkindxScoreIndex: index || 0,
            };
  
            dispatch({ type: 'SET_WALKINDEX_SCORES', payload: payload });
          }
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      handleLoading(index, false);
    }
  };

  const fetchSingleWalkIndex = async (latitude, longitude) => {
    let scores = await getRatings(
      latitude,
      longitude,
      getArea(currentDistance),
      userDetails?.email || LSEmail,
    );

    const isGoogleAds = searchParams.get('isGoogleAds') === 'true';

    if (scores) {
      if (!scores.Success && !Array.isArray(scores) && isGoogleAds) {
        if (scores.Error === 'Exceeded access limit') {
          dispatch({ type: 'SET_REACHED_MAX_LIMIT', payload: true });
        } else if (scores.Error === 'Unauthorized') {
          dispatch({ type: 'SET_TOKEN', payload: '' });
          dispatch({ type: 'SET_USER_DETAILS', payload: {} });
          navigate('/login');
        }
      } else {
        return scores[0];
      }
    }
  };

const findClosestNeighborhood = async (lat, lng) => {
  const data = await fetchNeighborhoodData();

  const point = turf.point([lng, lat]);
  let closestNeighborhood = null;
  let minDistance = Infinity;

  data.features.forEach((feature) => {
    const neighborhoodPoint = turf.point(feature.geometry.coordinates);
    const distance = turf.distance(point, neighborhoodPoint, { units: 'kilometers' });

    if (distance < minDistance) {
      minDistance = distance;
      closestNeighborhood = feature.properties.Name;
    }
  });

  return closestNeighborhood;
};

  // Fetch address data
  const fetchAddressData = async (address, checkLocation=true, shouldReturn=false) => {
    if (!address || address.trim() === '') return;

    try {
      const addressDetails = await fetchPlaceId(address, token);

      if (checkLocation) {
        isWithinCity(addressDetails?.address_components);
        if(!radiusParam) isInNYC(addressDetails);
      }

      const lat = addressDetails?.geometry?.location?.lat;
      const lng = addressDetails?.geometry?.location?.lng;

      const neighborhood = await findClosestNeighborhood(lat, lng);

      // const placeDetails = await fetchPlaceDetails(addressDetails.place_id, token);

      if (addressDetails && Object.keys(addressDetails).length > 0) {
        const propertyName = !isInLocalData(addressDetails?.formatted_address)
          ? addressDetails?.formatted_address
          : removeZipAndCountry(
              addressDetails?.formatted_address
            )?.toUpperCase();
        const payload = {
          placeID: addressDetails?.place_id,
          property: propertyName,
          latitude: lat,
          longitude: lng,
          sublocality: getSublocality(addressDetails),
          borough: getNeighborhood(addressDetails),
          neighborhood,
          postal: getPostalCode(addressDetails),
          shortName: addressDetails?.formatted_address?.split(',')[0].trim(),
          city: getAddressCity(addressDetails),
          state: getAddressState(addressDetails),
          country: getAddressCountry(addressDetails),
          photos: [],
        };

        saveAddressToRecentlyViewed(payload, recentlyViewedAddresses, dispatch);

        if (shouldReturn) return payload;

        dispatch({
          type: 'SET_CURRENT_ADDRESS',
          payload: payload,
        });
        
        // if (currentTab !== 'walkindex') {
        //   dispatch({ type: 'SET_MAP_TAB', payload: 'All' });
        // }
        
        dispatch({ type: 'SET_SHOW_MARKER_LIST', payload: false });
        dispatch({ type: 'SET_SHOW_MAP_QS', payload: false });
        dispatch({ type: 'SET_SHOW_FILTERS', payload: false });

        if (!showCompareLifeStyle) {
          dispatch({
            type: 'UPDATE_COMPARE_ADDRESS',
            payload: { index: 0, object: payload },
          });
          dispatch({
            type: 'UPDATE_LIFESTYLE_COMPARE_ADDRESS',
            payload: { item: 0, address: payload },
          });
        } else {
          console.log('Address already in comparison:', compareLifeStyleAddress);
        }
      }
      sendDataToGoogleAnalytics(`page_view`, {
        address_name: address
      });
      sendDataToGoogleAnalytics('address_visits', {
        address_name: address
      });
    } catch (error) {
      console.error('An error occurred:', error);
    } finally {
      await logGoogleServicesUsage(
        'places',
        `https://maps.googleapis.com/maps/api/geocode/json?address=${address}`,
        address,
        userDetails?.email || LSEmail || 'free_user',
      );
    }
  };

  const fetchAddressByLatLng = async (lat, lng, gotoExplore=false) => {
    const address = await getPlaceByLatLng(lat, lng);

    if (gotoExplore) {
      navigate(`/explore/?address=${address?.formatted_address}`);
    }

    isWithinCity(address?.address_components);

    const payload = {
      placeID: address?.place_id,
      property: address?.formatted_address,
      latitude: address?.geometry?.location?.lat,
      longitude: address?.geometry?.location?.lng,
      neighborhood: getNeighborhood(address),
      photos: [],
    };

    dispatch({
      type: 'SET_CURRENT_ADDRESS',
      payload: payload,
    });

    if (!showCompareLifeStyle) {
      dispatch({
        type: 'UPDATE_COMPARE_ADDRESS',
        payload: { index: 0, object: payload },
      });
      dispatch({
        type: 'UPDATE_LIFESTYLE_COMPARE_ADDRESS',
        payload: { item: 0, address: payload },
      });
    }

    dispatch({
      type: 'SET_SHOW_SEARCH_ADDRESS',
      payload: false,
    });
  };

  function getSublocality(response) {
    if (response) {
      for (let component of response.address_components) {
        if (component.types.includes('sublocality')) {
          return component.long_name;
        }
      }
    }
    return null;
  }

  const getNeighborhood = (response) => {
    if (response) {
      for (let component of response.address_components) {
        if (
          component.types.includes('neighborhood') ||
          component.types.includes('political')
        ) {
          return component.long_name;
        }
      }
    }
    return null;
  };

  const getPostalCode = (response) => {
    if (!response || !response.address_components) {
      return null;
    }

    const postalCodeComponent = response.address_components.find(
      component => component.types.includes('postal_code')
    );

    return postalCodeComponent ? postalCodeComponent.long_name : null;
  };

  const getAddressCity = (placeDetails) => {
    if (!placeDetails.address_components) {
      return null;
    }

    for (const component of placeDetails.address_components) {
      if (component.types.includes('administrative_area_level_1')) {
        return component.long_name;
      }
    }
    return null;
  };

  const getAddressCountry = (placeDetails) => {
    if (!placeDetails.address_components) {
      return null;
    }

    for (const component of placeDetails.address_components) {
      if (component.types.includes('country')) {
        return component.long_name;
      }
    }
    return null;
  };

  const getAddressState = (placeDetails) => {
    if (!placeDetails.address_components) {
      return null;
    }

    const stateComponent = placeDetails.address_components.find(component =>
      component.types.includes('administrative_area_level_1')
    );

    return stateComponent ? stateComponent.long_name : null;
  }

  const removeZipAndCountry = (address) => {
    // Split the address by commas
    let addressParts = address.split(',');
    // Remove the last two elements (ZIP code and country)
    addressParts.splice(addressParts.length - 2, 2);
    // Join the remaining parts back into a string
    let modifiedAddress = addressParts.join(',');
    // Remove New York, NY, and USA from the string
    modifiedAddress = modifiedAddress.replace(/,\s*(New York|NY|USA)/g, '');
    // Trim to remove extra spaces at the beginning or end
    return modifiedAddress.trim();
  };

  const isInLocalData = (address) => {
    return (
      propertiesData.filter((item) => item.property?.includes(address))
        ?.length > 0
    );
  };

  const isWithinCity = (addressComponents) => {
    const cityList = [
      'New York',
      'Washington',
      'Chicago',
      'Seattle',
      'Boston',
      'Los Angeles',
      'Austin',
      'Philadelphia',
      'Sioux Falls',
      'Massachusetts',
    ];
  
    const isCityFound = addressComponents.some((component) => {
      if (
        component.types?.includes('locality') ||
        component.types?.includes('administrative_area_level_1')
      ) {
        const isInCityList = cityList.includes(component.long_name);
        dispatch({ type: 'SET_IS_STANDALONE', payload: !isInCityList });
        return isInCityList;
      }
      return false;
    });
  
    if (!isCityFound) {
      dispatch({ type: 'SET_IS_STANDALONE', payload: true });
    }
  };

  const isInNYC = (data) => {
    const inNYC = data.address_components.some((component) => {
      return (
        (component.types.includes('locality') &&
          component.long_name === 'New York') ||
        (component.types.includes('administrative_area_level_1') &&
          component.long_name === 'New York' &&
          data.address_components.some(
            (comp) =>
              comp.types.includes('country') &&
              comp.long_name === 'United States'
          ))
      );
    });

    if (inNYC) {
      dispatch({ type: 'SET_IS_ADDRESS_IN_NYC', payload: true });
      if (!showCompareLifeStyle) {
        dispatch({ type: 'SET_CURRENT_DISTANCE', payload: '1' });
      }
    } else {
      dispatch({ type: 'SET_CURRENT_DISTANCE', payload: '3' });
      dispatch({ type: 'SET_IS_ADDRESS_IN_NYC', payload: false });
    }
  };

  const isBeta = () => {
    return location.pathname.startsWith('/beta');
  };

  const isCompass = () => {
    return location.pathname.startsWith('/compass');
  };

  // Fetch data when the component mounts
  useEffect(() => {
    const isGoogle = searchParams.get('is_google');
    const isFullScreen = searchParams.get('is_full_screen') || true;
    const longitude = searchParams.get('longitude');
    const latitude = searchParams.get('latitude');
    let address = searchParams.get('address');
    const type = searchParams.get('type') || 'Sale';
    const brandingColor = searchParams.get('branding');
    const plugin = searchParams.get('is_plugin');
    const font = searchParams.get('font');
    const view = searchParams.get('view');
    const addressChange = searchParams.get('address_change');
    const tutorial = searchParams.get('enable_tutorial');
    const client = searchParams.get('suffix');
    const city = searchParams.get('city');
    const urlIsAdmin = searchParams.get('isAdmin');
    const success = searchParams.get('success');
    const session_id = searchParams.get('session_id');

    const email = getLSData('Agent-Email');
    if (email) {
      dispatch({ type: 'SET_LS_EMAIL', payload: email });
    }

    if (session_id) {
      if (window.location.href.includes('http://localhost:3000/success')) {
        navigate('/success');
        return;
      }
    }

    // if (client && !address) {
    //   navigate('/address-not-found');
    //   return;
    // }

    if (city) {
      dispatch({ type: 'SET_CURRENT_CITY', payload: city });
    }

    const userData = getLSData('user-data');

    
    if (client) {
      dispatch({ type: 'SET_IS_ADMIN', payload: true });
      setLSData('Agent-Email', 'walkspanllc@gmail.com');
    } else {
    if (userData && !address) {
        handleLogin(userData)
      }
    }

    // Condition 2: userData is not present, address is present and URL includes '/plugin/'
    if (userData && address && location.pathname.includes("/plugin/")) {
      navigate(`/plugin/?address=${address}`);
    }

    if (location.pathname.includes("/plugin/")) {
      excludedPaths.push('/plugin/');
    }

    // Condition 3: userData is not present, URL doesn't include '/plugin/' and address is not present
    if (!userData && !isExcludedPath && !address) {
      navigate(`/login`);
    }

    // Condition 4: if address only
    if (address) {
      dispatch({ type: 'SET_SHOW_SEARCH_ADDRESS', payload: false });
      fetchAddressData(address);
    }

    const signupEmail = getLSData('signup-email');
    if (signupEmail) {
      dispatch({ type: 'SET_SIGNUP_EMAIL', payload: signupEmail });
    }

    const fpEmail = getLSData('fp-email');
    if (fpEmail) {
      dispatch({ type: 'SET_FORGOT_PASSWORD_EMAIL', payload: fpEmail });
    }

    // setAPIKey();

    // handleLogin();

    // const isLoggedIn = getCookieValue('isLoggedIn');
    // if (isLoggedIn) {
    //   const userToken = getLSData('user-token');
    //   dispatch({ type: 'SET_TOKEN', payload: userToken });
    //   const details = getLSData('user-details');
    //   if (details) {
    //     dispatch({ type: 'SET_USER_DETAILS', payload: details });
    //   }
    // } else {
    //   if (plugin === 'true' || isInFullAccessSites()) {
    //     handleLogin();
    //   } else {
    //     localStorage.removeItem('user-token');
    //     localStorage.removeItem('user-details');

    //     if (location.pathname.includes('/plugin')) {
    //       navigate('/login');
    //     } else {
    //       navigate('/');
    //     }
    //   }
    // }

     // Redirect to /plugin if plugin params are present but path is not already /plugin
    if (!location.pathname.startsWith('/plugin')) {
      if (plugin === 'true' || !!client) {
        const qs = queryString || window.location.search.slice(1);
        navigate(`/plugin/${qs ? '?' + qs : ''}`);
        return;
      }
    }

    try {
      const isEmbedded = window.self !== window.top;
      dispatch({ type: 'SET_EMBED_MODE', payload: isEmbedded });
    } catch (e) {
      dispatch({ type: 'SET_EMBED_MODE', payload: true });
    }

    if (location.pathname.startsWith('/address/')) {
      dispatch({ type: 'SET_SHOW_DASHBOARD', payload: false });
    }

    if (plugin === 'true') {
      dispatch({ type: 'SET_IS_PLUGIN', payload: true });
    }

    if (client) {
      dispatch({ type: 'SET_CLIENT_NAME', payload: client });
    }

    if (font) {
      getFontFamily(font);
    }

    if (view) {
      dispatch({
        type: 'SET_CURRENT_VIEW',
        payload: view,
      });
    }

    if (addressChange) {
      dispatch({
        type: 'SET_ENABLE_CHANGE_ADDRESS',
        payload: addressChange,
      });
    }

    if (tutorial) {
      dispatch({
        type: 'SET_ENABLE_TUTORIAL',
        payload: tutorial,
      });
    }

    if (isDeviceiPhone()) {
      dispatch({ type: 'SET_IS_IPHONE', payload: true });
    }

    const filteredProperties = propertiesData.filter(
      (item) => item.type === type
    );

    dispatch({
      type: 'SET_PROPERTY_LIST',
      payload: filteredProperties,
    });

    dispatch({
      type: 'SET_PARAMS',
      payload: { isFullScreen, isGoogle, longitude, latitude, address },
    });

    if (isFullScreen) {
      dispatch({ type: 'SET_SHOW_MODAL', payload: true });
    }

    if (brandingColor) {
      const hexWithoutHashRegex = /^([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})\b$/;
      const formattedBranding = hexWithoutHashRegex.test(brandingColor)
        ? `#${brandingColor}`
        : brandingColor;

      dispatch({ type: 'SET_BRANDING', payload: formattedBranding });
    }

    if (radiusParam) {
      const distance = getDistanceFromRadius(radiusParam);
      dispatch({ type: 'SET_CURRENT_DISTANCE', payload: distance });
    }

    getFavoriteMarkers();
    if (currentView === 'Modern') {
      getSavedAddresses();
      getRecentlyViewed();
    }
    dispatch({
      type: 'UPDATE_LIFESTYLE_COMPARE_ADDRESS',
      payload: { item: 0, address: currentAddress },
    });

    const handleFullScreenChange = () => {
      const payload =
        document.fullscreenElement ||
        document.mozFullScreenElement ||
        document.webkitFullscreenElement ||
        document.msFullscreenElement ||
        document.webkitCurrentFullScreenElement; // Safari and iOS;
      dispatch({ type: 'SET_IS_FULL_SCREEN_MAP', payload: payload });
    };

    document.addEventListener('fullscreenchange', handleFullScreenChange);
    document.addEventListener('mozfullscreenchange', handleFullScreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullScreenChange);
    document.addEventListener('msfullscreenchange', handleFullScreenChange);

    const handleResize = () => {
      const innerWidth = window.innerWidth;
      const isMobileNow = innerWidth < 768;
      dispatch({ type: 'SET_IS_MOBILE', payload: isMobileNow });

      const isSmallScreen = innerWidth <= 1280 && innerWidth > 768;
      dispatch({ type: 'SET_IS_SMALLER_SCREEN', payload: isSmallScreen });
    };

    window.addEventListener('resize', handleResize);

    handleResize();

    return () => {
      document.removeEventListener('fullscreenchange', handleFullScreenChange);
      document.removeEventListener(
        'mozfullscreenchange',
        handleFullScreenChange
      );
      document.removeEventListener(
        'webkitfullscreenchange',
        handleFullScreenChange
      );
      document.removeEventListener(
        'msfullscreenchange',
        handleFullScreenChange
      );

      window.removeEventListener('resize', handleResize);
    };
  }, []);


  useEffect(() => {
    if (currentAddress) {
      // const controller = new AbortController();
      // const signal = controller.signal;
      // if (!isMobile && !location.pathname.startsWith('/address/')) {
      //   fetchQuickSearchMarkers(currentAddress);
      // }
      document.title = `${currentAddress.property} - Walkspan`;
      // return () => {
      //   controller.abort(); // Cleanup: abort the fetch request
      // };
    }
  }, [currentAddress]);

  useEffect(() => {
    if (showNeighborhoodBounds && currentAddress) {
      handleNeighborhoodBorder(currentAddress.neighborhood);
      handleCityBorder(currentAddress.city);
    } else {
      dispatch({ type: 'SET_NEIGHBORHOOD_BOUNDS', payload: "" });
    }
  }, [showNeighborhoodBounds]);

  useEffect(() => {
    if (showCityBounds && currentAddress) {
      handleCityBorder(currentAddress.city);
    } else {
      dispatch({ type: 'SET_CITY_BOUNDS', payload: "" });
    }
  }, [showCityBounds]);

  const handleNeighborhoodBorder = async (neighborhood) => {
    const isFree = !hasUserData();
    const response = await getNeighborhoodBounds(neighborhood, isFree);
    console.log(response)
    dispatch({ type: 'SET_NEIGHBORHOOD_BOUNDS', payload: response?.data?.geojson });
  };

  const handleCityBorder = async (city) => {
    const isFree = !hasUserData();
    const response = await getCityBounds(city, isFree);
    console.log(response);
    dispatch({ type: 'SET_CITY_BOUNDS', payload: response?.data?.bounds });
  };

  const handleFetchScores = async (payload, index) => {
    if (currentTab === 'walkindex') {
      await fetchWalkIndexRatings(payload, index);
    }
  };

  const handleInitialFetchScores = async (payload, index) => {
    // await fetchLifeStyleScores(payload, index);
    await fetchWalkIndexRatings(payload, index);
  };

  useEffect(() => {
    if (
      currentAddress &&
      (isExploreEnabled
        ? true
        : showDashboard
      ) &&
      !location.pathname.startsWith('/address/')
    ) {
      handleInitialFetchScores();
    }
  }, [currentDistance, currentAddress, currentTab]);

  useEffect(() => {
    if (isMobile) {
      dispatch({ type: 'SET_SHOW_DASHBOARD', payload: false });
    }
  }, [isMobile]);

  useEffect(() => {
    if (!isFullScreenMap) {
      dispatch({ type: 'SET_SHOW_DASHBOARD', payload: true });
    }
  }, [isFullScreenMap]);

  const getQuickSearchResult = (markers, searchText) => {
    const filteredMarkers = markers?.filter((place) => {
      const placeCategory = place.category;
      const placeName = place.name?.toLowerCase();
      const category = placeCategory ? placeCategory.toLowerCase() : 'others';

      return searchText !== '' && (
        category.includes(searchText?.toLowerCase()) ||
        placeName.includes(searchText?.toLowerCase())
      );
    });
    if (isEstablishmentSearchOpen && filteredMarkers.length) {
      dispatch({
        type: 'SET_ESTABLISHMENT_SEARCH_RESULT',
        payload: { searchText: searchText, result: filteredMarkers.length },
      });
    }
    return filteredMarkers;
  };

  const handleFetchedMarkers = (markers, index=0) => {
    try {
      if (markers?.error === "50x") {
        dispatch({ type: 'SET_IS_SERVER_ERROR', payload: true });
      }

      if (markers) {
        if (!markers.Success && !Array.isArray(markers) && typeof markers === 'object') {
          if (markers.Error === 'Exceeded access limit') {
            dispatch({ type: 'SET_REACHED_MAX_LIMIT', payload: true });
          }
          else if (markers.Error === 'Unauthorized') {
            dispatch({ type: 'SET_TOKEN', payload: '' });
            dispatch({ type: 'SET_USER_DETAILS', payload: {} });
            navigate('/login');
          }
        } else {
          const data = convertMarkers(markers);

          const getLength = data ? data.length : 0;
          if (currentAddress !== null) {
            dispatch({
              type: 'UPDATE_COMPARE',
              payload: { number: index, length: getLength },
            });
          }
        }
      }
    } catch (error) {
      console.log(error)
    }
  };

  const fetchLifestyleGems = async (address, index=0, type, area) => {
    handleLoading(index, true);
    try {
      const { latitude, longitude } = address || currentAddress;
      const isFree = !hasUserData();
      const gems = await getLifeStyleEssentials(
        latitude,
        longitude,
        type,
        area,
        userDetails?.email || LSEmail,
        appendSuffix(),
        isFree,
      );

      if (
        gems?.status !== 200 &&
        gems?.data?.error &&
        !Array.isArray(gems)
      ) {
        // dispatch({ type: 'SET_ERROR_MESSAGE', payload: gems?.data?.error });
        // if (!showErrorModal) {
        //   dispatch({ type: 'SET_SHOW_ERROR_MODAL', payload: true });
        // }
        return;
      } else {
        if (gems?.error === "50x") {
          dispatch({ type: 'SET_IS_SERVER_ERROR', payload: true });
          handleLoading(index, false);
        }

        if (gems) {
          if (!gems.Success && !Array.isArray(gems)) {
            if (gems.Error === 'Exceeded access limit') {
              dispatch({ type: 'SET_REACHED_MAX_LIMIT', payload: true });
            }
          } else {
            // Remove duplicates first
            const uniqueGems = removeMarkerDuplicates(gems);

            // Count types
            const typeCounts = uniqueGems.reduce((acc, item) => {
              const type = item.type || 'others';
              acc[type] = (acc[type] || 0) + 1;
              return acc;
            }, {});

            const payload = {
              scoreData: typeCounts,
              scoreIndex: index || 0,
            };
            dispatch({ type: 'SET_LIFESTYLE_SCORES', payload: payload });

            return uniqueGems;
          }
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      handleLoading(index, false);
    }

  };

  const handleUnifiedSearch = async (serviceRef, type, mapCenter, radius = 402.43, dispatch) => {
    if (!serviceRef.current) {
      console.error("PlacesService is not initialized.");
      return [];
    }

    const nearbyRequest = {
      location: mapCenter,
      radius: radius, // Search within provided radius
      type, // For Nearby Search
    };

    const textRequest = {
      query: type, // For Text Search
      location: mapCenter,
      radius: radius, // Search within 5km radius
    };
  
    const fetchAllPages = (searchFunction, request) => {
      return new Promise((resolve, reject) => {
        let allResults = [];
  
        const processResults = (res, status, pagination) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK) {
            allResults = [...allResults, ...res];
  
            if (pagination && pagination.hasNextPage) {
              console.log("Fetching next page...");
              setTimeout(() => pagination.nextPage(), 2000); // Allow time for next page
            } else {
              resolve(allResults);
            }
          } else if (status === window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
            resolve([]); // No results found
          } else {
            console.error("Google Places API error:", status);
            reject(status);
          }
        };
  
        searchFunction(request, processResults);
      });
    };
  
    try {
      const [nearbyResults, textResults] = await Promise.all([
        fetchAllPages(serviceRef.current.nearbySearch.bind(serviceRef.current), nearbyRequest),
        fetchAllPages(serviceRef.current.textSearch.bind(serviceRef.current), textRequest),
      ]);
  
      const unifiedResults = [...nearbyResults, ...textResults];
      return unifiedResults;
    } catch (error) {
      console.error("Error during search:", error);
      return [];
    }
  };

  const fetchGooglePlacesMarkers = async (serviceRef, mapCenter, index=0) => {
    let googlePlacesResults = [];
    if (serviceRef?.current && mapCenter) {
      dispatch({ type: 'SET_IS_GOOGLE_PLACES_LOADED', payload: false });
      try {
        googlePlacesResults = await handleUnifiedSearch(
          serviceRef,
          'all',
          mapCenter,
          getRadius(currentDistance),
          dispatch
        );

        // Merge API and Google results
        const combinedResults = [];
        const findMatchingEntry = (item, list) =>
          list.find(
            (g) =>
              (g.vicinity &&
                item.address &&
                g.vicinity.toLowerCase() === item.address.toLowerCase()) ||
              (g.geometry?.location?.lat === item.lat &&
                g.geometry?.location?.lng === item.lng)
          );

        googlePlacesResults.forEach((place) => {
          combinedResults.push({
            id: place.place_id,
            name: place.name,
            address: place.vicinity,
            lat: place.geometry?.location?.lat(),
            lng: place.geometry?.location?.lng(),
            category: place.types?.[0]?.toca,
            is_24_7: false,
            is_preferred: false,
            opening_hours:
              place.opening_hours?.weekday_text?.join(', ') || 'Unknown',
            type: categorizePlaceType(place.types),
            rating: place.rating || null,
            user_ratings_total: place.user_ratings_total || null,
            google_place_id: place.place_id,
            google_icon: place.icon || null,
            source: 'google',
          });
        });

        const categoryCounts = combinedResults.reduce(
          (acc, place) => {
            const category = place.type || 'shops';
            acc[category] = (acc[category] || 0) + 1;
            return acc;
          },
          { food: 0, services: 0, shops: 0, transit: 0, leisure: 0 }
        );

        dispatch({
          type: 'SET_LIFESTYLE_SCORES',
          payload: { scoreData: categoryCounts, scoreIndex: index },
        });

        return combinedResults;
      } catch (error) {
        console.error('Google Places API Error:', error);
      } finally {
        dispatch({ type: 'SET_IS_GOOGLE_PLACES_LOADED', payload: true });
        handleLoading(index, false);
      }
    }
  };

  const fetchQuickSearchMarkers = async (address) => {
    let payload = {
      markers: [],
      isLoading: true,
    };
    dispatch({ type: 'SET_QUICK_SEARCH_MARKERS', payload: payload });
    let markers = await getLifeStyleEssentials(
      address.latitude,
      address.longitude,
      'all',
      'one-mile',
      userDetails?.email || LSEmail,
      appendSuffix(),
      isExploreEnabled,
    );
    const isGoogleAds = searchParams.get('isGoogleAds') === 'true';
    if (markers) {
      if (!markers.Success && !Array.isArray(markers) && isGoogleAds) {
        if (markers.Error === 'Exceeded access limit') {
          dispatch({ type: 'SET_REACHED_MAX_LIMIT', payload: true });
        } else if (markers.Error === 'Unauthorized') {
          dispatch({ type: 'SET_TOKEN', payload: '' });
          dispatch({ type: 'SET_USER_DETAILS', payload: {} });
          navigate('/login');
        }
      } else {
        const data = convertMarkers(markers);
        payload = {
          markers: data,
          isLoading: false,
        };
        dispatch({ type: 'SET_QUICK_SEARCH_MARKERS', payload: payload });
      }
    }
  };

  const normalizeCategories = (str) => {
      if (!str) return '';

      str = str?.toLowerCase().trim();
      if (!str) return '';
      if (str.length > 3 && !str.endsWith('ss') && !str.endsWith('us') && str.endsWith('s')) {
        return str.slice(0, -1) // remove only one trailing 's'
      }
      return str
  }

  // Normalize string by removing spaces to handle combined words like "drug store" vs "drugstore"
  const normalizeSpaces = (str) => {
    if (!str) return '';
    return str.toLowerCase().replace(/\s+/g, '');
  }

  const isCombinedWordMatch = (text, searchWord) => {
    const normalizedText = normalizeSpaces(text);
    const normalizedSearch = normalizeSpaces(searchWord);
    return normalizedText.includes(normalizedSearch);
  }

  const isWholeWordMatch = (text, searchWord) => {
    const regex = new RegExp(`\\b${searchWord}\\b`, 'i');
    return regex.test(text);
  };

  const isDeviceiPhone = () => {
    return (
      [
        'iPad Simulator',
        'iPhone Simulator',
        'iPod Simulator',
        'iPad',
        'iPhone',
        'iPod',
      ].includes(navigator.platform) ||
      // iPad on iOS 13 detection
      (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
    );
  };

  const getFontFamily = (fontStyle) => {
    const fontUrl = `https://fonts.googleapis.com/css?family=${fontStyle}`;
    const link = document.createElement('link');
    link.href = fontUrl;
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    document.documentElement.style.setProperty('--font-family', fontStyle);
  };

  const getBrandingColor = () => {
    const style = {
      '--color': branding !== null ? branding : '#000000',
    };
    return style;
  };

  const getLightBrandingColor = (lightnessIncrease) => {
    // Convert hex to hsl
    let hslColor = convert.hex.hsl(branding);

    // Increase lightness
    hslColor[2] += lightnessIncrease;
    hslColor[2] = Math.max(0, Math.min(hslColor[2], 100));

    // Convert hsl back to hex
    const lighterHex = convert.hsl.hex(hslColor);

    const style = {
      '--light-color': `#${lighterHex}`,
    };
    return style;
  };

  const fetchNeighborhoodList = async (borough) => {
    dispatch({
      type: 'SET_NEIGBORHOOD_LIST',
      payload: { data: [], isLoading: true }
    });
    try {
      const response = await getNeighborhoodByBorough(borough);
      dispatch({
        type: 'SET_NEIGBORHOOD_LIST',
        payload: { data: response, isLoading: false }
      });
      if (response?.length === 0) {
        dispatch({
          type: 'SET_ERROR_MESSAGE',
          payload: 'No records found for the selected location'
        }); 
      }
    } catch (error) {
      console.log(error);
    }  
  };

  const submitQuiz = async (payload) => {
    dispatch({
      type: 'SET_QUIZ_RESULTS',
      payload: { data: [], isLoading: true }
    });
    const response = await getQuizResults(token, payload);
    dispatch({
      type: 'SET_QUIZ_RESULTS',
      payload: { data: response, isLoading: false }
    });
  };

  const getSuffix = () => {
    const suffix = searchParams.get('suffix');
    const finalClient = suffix || clientName;
    return finalClient ? '_' + finalClient : '';
  };

  const setRatings = (number) => {
    // let newArray = currentRates;
    // if (currentRates.includes(number)) {
    //   newArray = currentRates.filter(rate => rate !== number);
    //   dispatch({ type: 'SET_RATES', payload: newArray });
    // } else {
    //   newArray = [...currentRates, number];
    //   dispatch({ type: 'SET_RATES', payload: newArray });
    // }
    dispatch({ type: 'SET_RATES', payload: [number] });

    sendDataToGoogleAnalytics('used_rating_filter', { rating: number });
  };

  const filterPreviousPage = () => {
    const page = previousPage[previousPage.length - 1];
    if (page === 1) {
      dispatch({ type: 'SET_SHOW_DASHBOARD', payload: true });
      dispatch({ type: 'SET_DESKTOP_SIDEBAR', payload: false });
      dispatch({ type: 'SET_COMPARE_LIFESTYLE', payload: false });
    } else if (page === 2) {
      dispatch({ type: 'SET_SHOW_DASHBOARD', payload: false });
      dispatch({ type: 'SET_COMPARE_LIFESTYLE', payload: false });
    } else {
      dispatch({ type: 'SET_SHOW_DASHBOARD', payload: false });
      dispatch({ type: 'SET_COMPARE_LIFESTYLE', payload: true });
    }
    const filteredPreviousPage =
      previousPage.length > 1 ? previousPage.slice(0, -1) : [];

    dispatch({
      type: 'FILTER_PREVIOUS_PAGE',
      payload: filteredPreviousPage,
    });
  };

  const areMapsLoading = () => {
    return isMapLoading.some((loading) => loading === true);
  };

  const clearErrorMessage = () => {
    if (errorMessage !== '') {
      dispatch({ type: 'SET_ERROR_MESSAGE', payload: '' });
    }

    if (signupError !== '') {
      dispatch({ type: 'SET_SIGNUP_ERROR', payload: '' });
    }
  };

  const sendDataToGoogleAnalytics = (eventName, params) => {
    window.gtag('event', eventName + getSuffix(), params);
  };

  const disableNeighborhoodAndCity = () => {
    if (showNeighborhoodBounds) {
      dispatch({
        type: 'SET_SHOW_NEIGHBORHOOD_BOUNDS',
        payload: false
      });
    }

    if (showCityBounds) {
      dispatch({
        type: 'SET_SHOW_CITY_BOUNDS',
        payload: false
      });
    }
  };

  const hasUserData = () => {
    const userData = getLSData('user-data');
    const AgentEmail = getLSData('Agent-Email');

    return (userData && userData.email) || (AgentEmail && AgentEmail.length > 0);
  };

  const fetchAllNewsletters = async () => {
    try {
      const response = await getAllNewsletters();
      dispatch({ type: 'SET_ANNOUNCEMENTS', payload: response });
      // console.log('Fetched newsletters:', response);
    } catch (error) {
      console.error("Error fetching newsletters:", error);
      return [];
    }
  };
  
  const actions = {
    handleLogin,
    handleLogout,
    fetchLifeStyleScores,
    fetchWalkIndexRatings,
    fetchSingleWalkIndex,
    setMapCenter,
    getAddressImage,
    fetchAddressData,
    getNeighborhood,
    getBrandingColor,
    getLightBrandingColor,
    toggleFullScreenMap,
    getQuickSearchResult,
    isWholeWordMatch,
    isCombinedWordMatch,
    normalizeCategories,
    sendDataToGoogleAnalytics,
    setRatings,
    filterPreviousPage,
    isInLocalData,
    areMapsLoading,
    fetchQuickSearchMarkers,
    isDeviceiPhone,
    fetchAddressByLatLng,
    handleForgotPassword,
    handleConfirmForgotPassword,
    handleSignup,
    handleResendSignup,
    handleConfirmSignup,
    clearErrorMessage,
    isInFullAccessSites,
    handleFetchedMarkers,
    getSavedAddresses,
    getRecentlyViewed,
    handleFetchScores,
    handleAPIKey,
    handleGoogleMarkerRating,
    fetchLifestyleGems,
    fetchGooglePlacesMarkers,
    disableNeighborhoodAndCity,
    handleUsageTracker,
    fetchAllNewsletters,
    fetchNeighborhoodList,
    submitQuiz,
  };

  return (
    <StoreContext.Provider value={{ state, dispatch, actions, navigate }}>
      {children}
    </StoreContext.Provider>
  );
};
